import type { Option } from './SelectServerDriven';
import type {
  Query,
  QueryClient,
  QueryKey,
  QueryOptions,
} from '@tanstack/react-query';
import type { LoadOptions } from 'react-select-async-paginate';

import ms from 'ms';
import { type GroupBase } from 'react-select';

type QueryParams = {
  [key: string]: any; // Allow for additional query parameters
  filterActive?: boolean;
  limit?: number;
  offset?: number;
  searchString?: string;
};

type QueryResult<T> = {
  data: T[];
  count: number;
};

type CreateLoadOptionsParams<T> = {
  createQueryKey: (params: QueryParams) => QueryKey;
  fetchItems: (params: QueryParams) => Promise<QueryResult<T>>;
  filterActive?: boolean;
  getOptionLabel?: (item: T) => string;
  getOptionValue?: (item: T) => string;
  pageSize?: number;
  queryClient: QueryClient;
  queryOptions?: Partial<QueryOptions>;
};

/**
 * Creates a loadOptions function for use with MultiSelectServerDriven (which is based on react-select-async-paginate).
 * This function handles pagination and search for a list of items.
 *
 * @template T - The type of the items being loaded.
 * @param {CreateLoadOptionsParams<T>} params - Configuration parameters for creating the loadOptions function.
 * @param {QueryClient} params.queryClient - The react-query client instance.
 * @param {function} params.createQueryKey - A function that creates the query key for fetching items.
 * @param {function} params.fetchItems - A function that fetches items from the API.
 * @param {function} params.getOptionLabel - A function that returns the label for an item.
 * @param {function} params.getOptionValue - A function that returns the value for an item.
 * @param {number} [params.pageSize] - Number of results per page.
 * @param {object} params.queryOptions - Additional query options.
 * @returns {LoadOptions<Option<T>, unknown, { page: number }>} A loadOptions function compatible with react-select-async-paginate.
 */
export function createLoadOptions<
  T,
  G extends GroupBase<Option<T>> = GroupBase<Option<T>>,
>({
  createQueryKey,
  fetchItems,
  filterActive,
  getOptionLabel = ({ name }) => name,
  getOptionValue = ({ id }) => id,
  pageSize,
  queryClient,
  queryOptions = {},
}: CreateLoadOptionsParams<T>): LoadOptions<
  Option<T>,
  G,
  {
    filterActive?: boolean;
    page: number;
  }
> {
  return async (searchString, _, { page }) => {
    const queryParams: QueryParams = {
      filterActive,
      offset: (page - 1) * (pageSize ?? 100),
      searchString,
    };
    const queryKey = createQueryKey(queryParams);

    try {
      const { data, hasNextPage } = await queryClient.fetchQuery({
        queryFn: async () => fetchItems(queryParams),
        queryKey,
        ...queryOptions,
      });

      return {
        additional: {
          filterActive,
          page: Number(page) + 1,
        },
        hasMore: hasNextPage,
        options:
          data?.map((item: T): Option<T> => ({
            data: item,
            label: getOptionLabel(item),
            value: getOptionValue(item),
          })) ?? [],
      };
    } catch (error) {
      console.error('Error loading items:', error);

      return {
        hasMore: false,
        options: [],
      };
    }
  };
}

type CreateGetItemDataParams<T> = {
  createQueryKey: (id: string) => unknown[];
  fetchItem: (id: string) => Promise<T>;
  getItemId?: (item: T) => string;
  queryClient: QueryClient;
  queryKeyBase: string[];
};

type QueryDataWithItems = {
  data?: unknown[];
  items?: unknown[];
};

/**
 * Creates a function to retrieve item data based on React Query,
 * with a multi-level caching strategy and fallback to API fetch.
 *
 * @template T - The type of the item being retrieved.
 * @param {CreateGetItemDataParams<T>} params - Configuration parameters for creating the getItemData function.
 * @returns {function} A function that retrieves item data given an ID, following these steps:
 *   1. Checks the individual item cache.
 *   2. If not found, searches in the list cache.
 *   3. If still not found, fetches from the API.
 *   4. Returns undefined if an error occurs or if the ID is falsy.
 */
export function createGetItemData<T>({
  createQueryKey,
  fetchItem,
  getItemId = ({ id }) => id,
  queryClient,
  queryKeyBase,
}: CreateGetItemDataParams<T>) {
  return async (id: string): Promise<T | undefined> => {
    if (!id) {
      return undefined;
    }

    const queryKey = createQueryKey(id);

    try {
      // First, try to get the item from the cache
      const cachedItem = queryClient.getQueryData<T>(queryKey);
      if (cachedItem) {
        return cachedItem;
      }

      // If not in cache, check if it's in the list cache
      const queriesData = queryClient.getQueriesData<QueryDataWithItems>({
        exact: false,
        queryKey: queryKeyBase,
      });

      for (const [, queryData] of queriesData) {
        if (!queryData || typeof queryData !== 'object') continue;

        // Handle both data and items property names
        const items = Array.isArray(queryData.data)
          ? queryData.data
          : Array.isArray(queryData.items)
            ? queryData.items
            : null;

        if (items) {
          const item = items.find(
            (item) =>
              item && typeof item === 'object' && getItemId(item as T) === id,
          );

          if (item) {
            return item as T;
          }
        }
      }

      // If there was an error fetching the item (e.g. 403 error), wait at least 60 seconds before trying again
      const queryState = queryClient.getQueryState<Query<T>>(queryKey);
      const queryError = queryState?.errorUpdatedAt ?? 0;
      const querySuccess = queryState?.dataUpdatedAt ?? 0;
      const lastAttemptWasError =
        (Boolean(queryError) && !querySuccess) ||
        (Boolean(queryError) &&
          Boolean(querySuccess) &&
          queryError > querySuccess);
      const lastAttemptFailedRecently =
        lastAttemptWasError && queryError > Date.now() - ms('60s');

      if (lastAttemptFailedRecently) {
        return undefined;
      }

      // If not found in any cache, fetch the item
      const item = await queryClient.fetchQuery<T>({
        queryFn: async () => fetchItem(id),
        queryKey,
      });

      return item;
    } catch (error) {
      console.error('Error fetching item data:', error);
      return undefined;
    }
  };
}
